<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import BalancedLinesDisplay from './BalancedLinesDisplay.vue'
import BalancedLinesModal from './BalancedLinesModal.vue'

const props = defineProps({
  fixtureId: {
    type: [String, Number],
    required: true
  },
  bookmakerId: {
    type: [String, Number],
    default: 83
  }
})

const apiUrl = 'https://go-monitoring-tool.tools.oddscomposer.io/api/events/list'
const data = ref(null)
const loading = ref(false)
const error = ref(null)
const isInitialLoad = ref(true)
const pollingInterval = ref(null)
const showModal = ref(false)
const selectedLineType = ref(null) // 'ou1Lines' or 'ou2Lines'

// Calculate the most balanced line (where odds difference is smallest)
const findMostBalancedLine = (lines) => {
  if (!lines || typeof lines !== 'object') return null
  
  let mostBalanced = null
  let smallestDiff = Infinity
  
  for (const [line, odds] of Object.entries(lines)) {
    if (odds && typeof odds === 'object' && odds.over && odds.under) {
      const diff = Math.abs(odds.over - odds.under)
      if (diff < smallestDiff) {
        smallestDiff = diff
        mostBalanced = {
          line: parseFloat(line),
          over: odds.over,
          under: odds.under,
          diff: diff
        }
      }
    }
  }
  
  return mostBalanced
}

// Fetch data from API
const fetchData = async () => {
  if (!props.fixtureId) return
  
  // Only show loading on initial load to prevent flicker on subsequent polls
  if (isInitialLoad.value) {
    loading.value = true
  }
  error.value = null
  
  try {
    // Build URL with query parameters
    const params = new URLSearchParams({
      format: 'json',
      fixture_id: props.fixtureId.toString(),
      bookmaker_id: props.bookmakerId.toString()
    })
    const urlWithParams = `${apiUrl}?${params.toString()}`
    
    // Use fetch instead of axios to avoid any default headers that might trigger preflight
    const response = await fetch(urlWithParams, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      credentials: 'omit', // Don't send credentials
      mode: 'cors' // Explicitly set CORS mode
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    const responseData = await response.json()
    
    if (responseData && responseData.events && responseData.events.length > 0) {
      const event = responseData.events[0]
      const newOu1Balanced = findMostBalancedLine(event.ou1Lines)
      const newOu2Balanced = findMostBalancedLine(event.ou2Lines)
      
      // Only update if data has actually changed to prevent flickering
      const hasChanged = !data.value || 
        JSON.stringify(newOu1Balanced) !== JSON.stringify(data.value.ou1Balanced) ||
        JSON.stringify(newOu2Balanced) !== JSON.stringify(data.value.ou2Balanced) ||
        event.updatedAt !== data.value.event?.updatedAt
      
      if (hasChanged) {
        data.value = {
          event: event,
          ou1Balanced: newOu1Balanced,
          ou2Balanced: newOu2Balanced,
          timestamp: responseData.timestamp
        }
      }
    } else {
      // Only set to null if we had data before (avoid flicker on initial load)
      if (data.value !== null) {
        data.value = null
      }
    }
  } catch (err) {
    console.error('Error fetching balanced lines:', err)
    error.value = err.message || 'Failed to fetch data'
    // Only set to null if we had data before
    if (data.value !== null) {
      data.value = null
    }
  } finally {
    loading.value = false
    isInitialLoad.value = false
  }
}

// Start polling
const startPolling = () => {
  fetchData() // Fetch immediately
  pollingInterval.value = setInterval(() => {
    fetchData()
  }, 5000) // Poll every 5 seconds
}

// Stop polling
const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value)
    pollingInterval.value = null
  }
}

const openModal = () => {
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
}

// Watch fixtureId to restart polling when it changes
watch(() => props.fixtureId, () => {
  stopPolling()
  isInitialLoad.value = true // Reset initial load flag when fixture changes
  data.value = null // Clear data when fixture changes
  startPolling()
})

onMounted(() => {
  startPolling()
})

onUnmounted(() => {
  stopPolling()
})
</script>

<template>
  <div class="balanced-lines-polling h-full">
    <BalancedLinesDisplay
      v-if="data && !loading"
      :ou1-balanced="data.ou1Balanced"
      :ou2-balanced="data.ou2Balanced"
      :event="data.event"
      @open-modal="openModal"
    />
    
    <div v-if="loading && !data" class="text-center py-2 text-gray-500 text-xs h-full flex items-center justify-center">
      <i class="fas fa-spinner fa-spin mr-2"></i>
      Loading lines...
    </div>
    
    <div v-if="error" class="text-center py-2 text-red-500 text-xs h-full flex items-center justify-center">
      <i class="fas fa-exclamation-circle mr-2"></i>
      {{ error }}
    </div>
    
    <BalancedLinesModal
      v-if="showModal && data"
      :show="showModal"
      :event="data.event"
      :ou1-balanced="data.ou1Balanced"
      :ou2-balanced="data.ou2Balanced"
      @close="closeModal"
    />
  </div>
</template>

<style scoped>
.balanced-lines-polling {
  /* Component styles */
}
</style>
